import React, { useEffect } from 'react'
import { Button } from 'antd'
import AppLayout from 'components/layout/layout'
import { capitalizeFirstLetter } from 'helper/utility'
import { useParams } from 'react-router-dom'
import SkillsTable from './Tables/SkillsTable'
import { skillCreateStore } from 'state/listing/skillListing'
import { infoMessage } from 'api/global'
import SkillModal from './Modals/SkillModal'
import GradesTable from './Tables/GradesTable'
import { gradeStore } from 'state/listing/gradeListing'
import GradeModal from './Modals/GradeModal'
import RelationsTable from './Tables/RelationsTable'
import { relationStore } from 'state/listing/relationListing'
import RelationModal from './Modals/RelationModal'
import EventCategoriesTable from './Tables/EventCategoriesTable'
import { categoryStore } from 'state/listing/eventCategoryListing'
import EventCategoryModal from './Modals/EventCategoryModal'
import ColorTable from './Tables/ColorTable'
import { colorStore } from 'state/listing/colorListing'
import ColorModal from './Modals/ColorModal'
import { activityCategoryStore } from 'state/listing/activityCategoryListing'
import ActivityCategoriesTable from './Tables/ActivityCategoriesTable'
import ActivityCategoryModal from './Modals/ActivityCategoryModal'
import GoalTable from './Tables/GoalTable'
import { goalStore } from 'state/listing/GoalListing'
import GoalModal from './Modals/GoalModal'
import HighlightedElementsTable from './Tables/HighlightedElements'
import { highlightedElementStore } from 'state/listing/highlightedElementListing'
import HighlightedElemModal from './Modals/HighlightedElemModal'
import AgegroupTable from './Tables/AgegroupTable'
import { ageGroupStore } from 'state/listing/ageGroupListing'
import AgeGroupModal from './Modals/AgeGroupModal'
import ItemsToBring from './Tables/ItemsToBring'
import { itemsToBringStore } from 'state/listing/itemsToBringListing'
import ItemsToBringModal from './Modals/ItemsToBringModal'
import AddDefaultIconModal from './Modals/AddDefaultIconModal'
import { defaultIconStore } from 'state/listing/defaultIcon'
import { useGetDefaultIconByType } from 'api/Listings/DefaultIcon'
import { DefaultIconType } from 'types/listing/listings'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { hasCustomIcon } from './constants'
import EnergyLevel from './Tables/EnergyLevel'
import { energyLevelStore } from 'state/listing/energyLevel'
import EnergyLevelModal from './Modals/EnergyLevelModal'
import BreadCrumbs from 'components/global/BreadCrumbs'
import LeagueSportsTable from './Tables/LeagueSportsTable'
import { sportCreationStore } from 'state/listing/sportListing'
import LeagueSportModal from './Modals/LeagueSportModal'
import AmenitiesTable from './Tables/AmenitiesTable'
import { amenitiesCreationStore } from 'state/listing/amenitiesListing'
import AmenitiesModal from './Modals/AmenitiesModal'

const SpecificListingPage: React.FC = () => {
  const { id }: { id: string } = useParams()
  const { addSkillModal, setAddSkillModal, setSkillData } = skillCreateStore(
    store => ({
      addSkillModal: store.addSkillModal,
      setAddSkillModal: store.setAddSkillModal,
      setSkillData: store.setSkillData,
    }),
  )
  const { addGradeModal, setAddGradeModal, setGradeData } = gradeStore(
    store => ({
      addGradeModal: store.addGradeModal,
      setAddGradeModal: store.setAddGradeModal,
      setGradeData: store.setGradeData,
    }),
  )
  const { addRelationModal, setAddRelationModal, setRelationData } =
    relationStore(store => ({
      addRelationModal: store.addRelationModal,
      setAddRelationModal: store.setAddRelationModal,
      setRelationData: store.setRelationData,
    }))
  const {
    addEventCategoryModal,
    setAddEventCategoryModal,
    setEventCategoryData,
  } = categoryStore(store => ({
    addEventCategoryModal: store.addEventCategoryModal,
    setAddEventCategoryModal: store.setAddEventCategoryModal,
    setEventCategoryData: store.setEventCategoryData,
  }))
  const { addColorModal, setAddColorModal, setColorListingData } = colorStore(
    store => ({
      addColorModal: store.addColorModal,
      setAddColorModal: store.setAddColorModal,
      setColorListingData: store.setColorListingData,
    }),
  )
  const {
    addActivityCategoryModal,
    setAddActivityCategoryModal,
    setActivityCategoryListingData,
  } = activityCategoryStore(store => ({
    addActivityCategoryModal: store.addActivityCategoryModal,
    setAddActivityCategoryModal: store.setAddActivityCategoryModal,
    setActivityCategoryListingData: store.setActivityCategoryListingData,
  }))
  const { addGoalModal, setAddGoalModal, setGoalData } = goalStore(store => ({
    addGoalModal: store.addGoalModal,
    setAddGoalModal: store.setAddGoalModal,
    setGoalData: store.setGoalData,
  }))
  const {
    addHighlightedElementModal,
    setAddHighlightedElementModal,
    setHighlightedElementData,
  } = highlightedElementStore(store => ({
    addHighlightedElementModal: store.addHighlightedElementModal,
    setAddHighlightedElementModal: store.setAddHighlightedElementModal,
    setHighlightedElementData: store.setHighlightedElementData,
  }))
  const { addAgeGroupModal, setAddAgeGroupModal, setAgeGroupData } =
    ageGroupStore(store => ({
      addAgeGroupModal: store.addAgeGroupModal,
      setAddAgeGroupModal: store.setAddAgeGroupModal,
      setAgeGroupData: store.setAgeGroupData,
    }))
  const { addItemsToBringModal, setAddItemsToBringModal, setItemsToBringData } =
    itemsToBringStore(store => ({
      addItemsToBringModal: store.addItemsToBringModal,
      setAddItemsToBringModal: store.setAddItemsToBringModal,
      setItemsToBringData: store.setItemsToBringData,
    }))
  const { energyLevelModal, setEnergyLevelModal, setEnergyLevelData } =
    energyLevelStore(store => ({
      energyLevelModal: store.energyLevelModal,
      setEnergyLevelModal: store.setEnergyLevelModal,
      setEnergyLevelData: store.setEnergyLevelData,
    }))

  const {
    addDefaultIconModal,
    setAddDefaultIconModal,
    setIconFor,
    setDefaultData,
  } = defaultIconStore(store => ({
    addDefaultIconModal: store.addDefaultIconModal,
    setAddDefaultIconModal: store.setAddDefaultIconModal,
    setIconFor: store.setIconFor,
    setDefaultData: store.setDefaultData,
  }))

  const { addSportModal, setAddSportModal, setSportData } = sportCreationStore(
    store => ({
      addSportModal: store.addSportModal,
      setAddSportModal: store.setAddSportModal,
      setSportData: store.setSportData,
    }),
  )

  const {
    addAmenitiesModal,
    setAddAmenitiesModal,
    setAmenitiesData,
  } = amenitiesCreationStore(store => ({
    addAmenitiesModal: store.addAmenitiesModal,
    setAddAmenitiesModal: store.setAddAmenitiesModal,
    setAmenitiesData: store.setAmenitiesData,
  }))

  const {
    data: defaultIconData,
    isLoading: defaultIconLoading,
    isFetching: defaultIconFetching,
    refetch: defaultIconRefetch,
  } = useGetDefaultIconByType({ iconFor: id as DefaultIconType })

  const hanldeAddDefualtIcon = () => {
    switch (id) {
      case 'item-to-bring':
        setDefaultData(defaultIconData ? defaultIconData : null)
        setAddDefaultIconModal(true)
        setIconFor('item-to-bring')
        break
      default:
        infoMessage('Needs to be handled')
        break
    }
  }

  const handleNewClick = () => {
    switch (id) {
      case 'skill':
        setSkillData(null)
        setAddSkillModal(true)
        break
      case 'grade':
        setGradeData(null)
        setAddGradeModal(true)
        break
      case 'relation':
        setRelationData(null)
        setAddRelationModal(true)
        break
      case 'event-category':
        setEventCategoryData(null)
        setAddEventCategoryModal(true)
        break
      case 'activity-category':
        setActivityCategoryListingData(null)
        setAddActivityCategoryModal(true)
        break
      case 'colors':
        setColorListingData(null)
        setAddColorModal(true)
        break
      case 'goal':
        setGoalData(null)
        setAddGoalModal(true)
        break
      case 'highlighted-element':
        setHighlightedElementData(null)
        setAddHighlightedElementModal(true)
        break
      case 'age-group':
        setAgeGroupData(null)
        setAddAgeGroupModal(true)
        break
      case 'item-to-bring':
        setItemsToBringData(null)
        setAddItemsToBringModal(true)
        break
      case 'energy-level':
        setEnergyLevelData(null)
        setEnergyLevelModal(true)
        break
      case 'league-sport':
        setSportData(null)
        setAddSportModal(true)
        break
      case 'amenities':
        setAmenitiesData(null)
        setAddAmenitiesModal(true)
        break
      default:
        infoMessage('Selected modal not found')
        break
    }
  }

  const handleComponent = () => {
    switch (id) {
      case 'skill':
        return <SkillsTable id={id} />
      case 'grade':
        return <GradesTable id={id} />
      case 'relation':
        return <RelationsTable id={id} />
      case 'event-category':
        return <EventCategoriesTable id={id} />
      case 'activity-category':
        return <ActivityCategoriesTable id={id} />
      case 'colors':
        return <ColorTable id={id} />
      case 'goal':
        return <GoalTable id={id} />
      case 'highlighted-element':
        return <HighlightedElementsTable id={id} />
      case 'age-group':
        return <AgegroupTable id={id} />
      case 'item-to-bring':
        return <ItemsToBring id={id} />
      case 'energy-level':
        return <EnergyLevel id={id} />
      case 'league-sport':
        return <LeagueSportsTable id={id} />
      case 'amenities':
        return <AmenitiesTable id={id} />
      default:
        return <div>Component not found</div>
    }
  }

  useEffect(() => {
    const hasCustomIconEnabled = hasCustomIcon.includes(id)
    if (id && hasCustomIconEnabled) {
      defaultIconRefetch()
    }
  }, [id])

  return (
    <AppLayout id={id}>
      <div className="container px-40px py-40px scroller overflow-auto-y">
        <div className="mb-40">
          <BreadCrumbs
            links={[
              {
                name: 'Listings',
                link: '/settings?tab=listings',
              },
              {
                name: `${capitalizeFirstLetter(id.replace(/-/g, ' '))}`,
                link: `/manage-listing/${id}`,
              },
            ]}
          />
        </div>
        <div className="flex align-center justify-between">
          <h1 className="text-24 w-600">
            {capitalizeFirstLetter(id.replace(/-/g, ' '))}
          </h1>
          <div className="flex gap-10px">
            <Button className={'btn-new primary br-8'} onClick={handleNewClick}>
              Add New
            </Button>
            {id === 'item-to-bring' && (
              <Button
                className={'btn-default-icon br-8'}
                onClick={hanldeAddDefualtIcon}
              >
                {defaultIconLoading || defaultIconFetching ? (
                  <ButtonSpinner size={20} color="#2ba7ca" />
                ) : defaultIconData ? (
                  <div className="flex gap-10px align-center">
                    <img
                      src={defaultIconData.icon.thumb}
                      width={20}
                      height={20}
                    />
                    <span className="text-14 w-500 text-prim">Update Icon</span>
                  </div>
                ) : (
                  'Add Default Icon'
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-20px">{handleComponent()}</div>
      </div>

      {addSkillModal && <SkillModal />}
      {addGradeModal && <GradeModal />}
      {addRelationModal && <RelationModal />}
      {addEventCategoryModal && <EventCategoryModal />}
      {addColorModal && <ColorModal />}
      {addActivityCategoryModal && <ActivityCategoryModal />}
      {addGoalModal && <GoalModal />}
      {addHighlightedElementModal && <HighlightedElemModal />}
      {addAgeGroupModal && <AgeGroupModal />}
      {addItemsToBringModal && <ItemsToBringModal />}
      {addDefaultIconModal && <AddDefaultIconModal />}
      {energyLevelModal && <EnergyLevelModal />}
      {addSportModal && <LeagueSportModal />}
      {addAmenitiesModal && <AmenitiesModal />}
    </AppLayout>
  )
}

export default SpecificListingPage
